<template>
  <div :class="toolbarClass">
    <template v-for="(group, groupIndex) in groupedToolbarActions" :key="group.key">
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-for="action in group.actions"
          :key="action.key"
          :size="buttonSize"
          :variant="buttonVariant"
          type="button"
          :title="action.label"
          :aria-label="action.label"
          :disabled="resolveActionDisabled(action)"
          :class="[
            resolveActionActive(action) ? activeButtonClass : null,
          ]"
          @click="emit('action', action)"
        >
          <component v-if="action.icon" :is="action.icon" class="h-4 w-4" aria-hidden="true" />
          <span v-else class="text-xs font-medium">{{ action.label }}</span>
          <span class="sr-only">{{ action.label }}</span>
        </Button>
      </div>

      <div
        v-if="groupIndex < groupedToolbarActions.length - 1 || overflowActions.length > 0"
        class="h-7 w-px shrink-0 bg-border"
        aria-hidden="true"
      />
    </template>

    <DropdownMenu v-if="overflowActions.length > 0">
      <DropdownMenuTrigger as-child>
        <Button
          :size="buttonSize"
          :variant="buttonVariant"
          type="button"
          title="More actions"
          aria-label="More actions"
        >
          <MoreHorizontal class="h-4 w-4" aria-hidden="true" />
          <span class="sr-only">More actions</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" class="w-52">
        <template v-for="(group, groupIndex) in groupedOverflowActions" :key="group.key">
          <DropdownMenuItem
            v-for="action in group.actions"
            :key="action.key"
            :disabled="resolveActionDisabled(action)"
            @select="handleOverflowSelect($event, action)"
          >
            <component v-if="action.icon" :is="action.icon" class="mr-2 h-4 w-4" aria-hidden="true" />
            <span>{{ action.label }}</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator
            v-if="groupIndex < groupedOverflowActions.length - 1"
          />
        </template>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { MoreHorizontal } from 'lucide-vue-next'
import { Button } from '../../ui/button/index.js'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu/index.js'

const props = defineProps({
  actions: {
    type: Array,
    required: true,
  },
  isActionDisabled: {
    type: Function,
    default: () => false,
  },
  isActionActive: {
    type: Function,
    default: () => false,
  },
  toolbarClass: {
    type: String,
    default: 'flex flex-wrap items-center gap-2',
  },
  buttonSize: {
    type: String,
    default: 'sm',
  },
  buttonVariant: {
    type: String,
    default: 'outline',
  },
  activeButtonClass: {
    type: String,
    default: 'bg-accent text-accent-foreground',
  },
})

const emit = defineEmits(['action'])

const normalizePlacement = (action) => {
  return action?.placement === 'overflow' ? 'overflow' : 'toolbar'
}

const buildActionGroups = (actionList) => {
  const groups = []
  const groupIndexMap = new Map()

  for (const action of actionList) {
    if (!action || typeof action !== 'object') {
      continue
    }

    const groupKey = typeof action.group === 'string' && action.group.trim() !== ''
      ? action.group
      : 'default'

    if (!groupIndexMap.has(groupKey)) {
      groupIndexMap.set(groupKey, groups.length)
      groups.push({
        key: groupKey,
        actions: [],
      })
    }

    groups[groupIndexMap.get(groupKey)].actions.push(action)
  }

  return groups
}

const toolbarActions = computed(() => {
  return props.actions.filter((action) => normalizePlacement(action) === 'toolbar')
})

const overflowActions = computed(() => {
  return props.actions.filter((action) => normalizePlacement(action) === 'overflow')
})

const groupedToolbarActions = computed(() => {
  return buildActionGroups(toolbarActions.value)
})

const groupedOverflowActions = computed(() => {
  return buildActionGroups(overflowActions.value)
})

const resolveActionDisabled = (action) => {
  return props.isActionDisabled(action)
}

const resolveActionActive = (action) => {
  return props.isActionActive(action)
}

const handleOverflowSelect = (event, action) => {
  event?.preventDefault?.()
  emit('action', action)
}
</script>
